import * as React from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Title from "../Common/Title";
import { getInquiredProducts } from "../../Utils/vendor-customer-service";
import {
  extractIdFromGid,
  SHOPIFY_PRODUCT_STORE_URL,
} from "../../Utils/constant";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const PAGE_SIZE = 20;

const TIME_WINDOWS = [
  { label: "15 days", value: 15 },
  { label: "30 days", value: 30 },
  { label: "90 days", value: 90 },
  { label: "180 days", value: 180 },
  { label: "All time", value: null },
];

const DEFAULT_DAYS = 30;

const windowLabel = (days) => {
  if (days == null) {
    return "all-time";
  }
  return `last ${days} days`;
};

const openShopifyProduct = (productGid) => {
  const productId = extractIdFromGid(productGid);
  if (!SHOPIFY_PRODUCT_STORE_URL || !productId) {
    return;
  }
  const url = `${SHOPIFY_PRODUCT_STORE_URL}/${productId}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

export default function InquiredProducts() {
  const [days, setDays] = React.useState(DEFAULT_DAYS);
  const [items, setItems] = React.useState([]);
  const [offset, setOffset] = React.useState(0);
  const [totalCount, setTotalCount] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [initialLoading, setInitialLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [lightbox, setLightbox] = React.useState({
    open: false,
    src: "",
    title: "",
  });

  const openLightbox = (src, title) => {
    if (!src) {
      return;
    }
    setLightbox({ open: true, src, title: title || "Product" });
  };

  const closeLightbox = () => {
    setLightbox({ open: false, src: "", title: "" });
  };

  const fetchPage = React.useCallback(
    (nextOffset, replace = false, daysWindow = days) => {
      return getInquiredProducts(PAGE_SIZE, nextOffset, { days: daysWindow })
        .then((response) => {
          if (response?.status !== 200) {
            setError("Unable to load inquired products.");
            setHasMore(false);
            return;
          }

          const nodes = response?.data?.nodes || [];
          const count = response?.data?.count ?? 0;
          const pageInfo = response?.data?.pageInfo || {};
          const next = pageInfo.nextOffset ?? nextOffset + nodes.length;

          setItems((prev) => (replace ? nodes : [...prev, ...nodes]));
          setOffset(next);
          setTotalCount(count);
          setHasMore(Boolean(pageInfo.hasNextPage));
          setError("");
        })
        .catch(() => {
          setError("Unable to load inquired products.");
          setHasMore(false);
        })
        .finally(() => {
          setInitialLoading(false);
        });
    },
    [days],
  );

  React.useEffect(() => {
    setItems([]);
    setOffset(0);
    setTotalCount(0);
    setHasMore(true);
    setInitialLoading(true);
    setError("");
    fetchPage(0, true, days);
  }, [days, fetchPage]);

  const loadMore = () => {
    if (!hasMore) {
      return;
    }
    fetchPage(offset, false, days);
  };

  const selectTimeWindow = (value) => {
    if (value === days) {
      return;
    }
    setDays(value);
  };

  return (
    <React.Fragment>
      <Title>Inquired Products</Title>
      <Box sx={{ mt: 1, mb: 2 }}>
        <Typography variant="body1">
          Products from estimate-again usage across vendors ({windowLabel(days)}
          )
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 1.5, mb: 1 }}
        >
          {TIME_WINDOWS.map((option) => {
            const selected = option.value === days;
            return (
              <Chip
                key={option.label}
                label={option.label}
                clickable
                color={selected ? "primary" : "default"}
                variant={selected ? "filled" : "outlined"}
                onClick={() => selectTimeWindow(option.value)}
              />
            );
          })}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          Showing {items.length}
          {totalCount ? ` of ${totalCount}` : ""} loaded
          {hasMore ? " (more available)" : ""}
        </Typography>
      </Box>

      {error ? <Typography color="error">{error}</Typography> : null}

      {initialLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          loader={
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          }
          endMessage={
            <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
              {items.length === 0
                ? "No inquired products in this time window."
                : "All inquired products loaded."}
            </Typography>
          }
        >
          <Grid container spacing={2} sx={{ pb: 4 }}>
            {items.map((product) => {
              const imageSrc = product?.featuredImage?.url;
              const title = product?.unavailable
                ? "Product unavailable"
                : product?.title || "Untitled product";

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {imageSrc ? (
                      <CardMedia
                        component="img"
                        height="200"
                        image={imageSrc}
                        alt={
                          product?.featuredImage?.altText ||
                          product?.title ||
                          "Product"
                        }
                        onClick={() => openLightbox(imageSrc, product?.title)}
                        sx={{
                          cursor: "pointer",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: "grey.100",
                        }}
                      >
                        <Typography color="text.secondary">
                          {product?.unavailable ? "Unavailable" : "No image"}
                        </Typography>
                      </Box>
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Chip
                          size="small"
                          color="primary"
                          label={`#${product?.rank ?? "—"}`}
                        />
                        {product?.status ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={product.status}
                          />
                        ) : null}
                        {product?.unavailable ? (
                          <Chip size="small" color="warning" label="Missing" />
                        ) : null}
                      </Stack>
                      <Typography variant="subtitle1" gutterBottom>
                        {title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Estimates: {product?.estimateCount ?? 0}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Vendors: {product?.vendorCount ?? 0}
                      </Typography>
                      {product?.totalInventory != null ? (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Inventory: {product.totalInventory}
                        </Typography>
                      ) : null}
                    </CardContent>
                    <CardActions
                      sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        disabled={
                          !SHOPIFY_PRODUCT_STORE_URL ||
                          !extractIdFromGid(product?.id)
                        }
                        onClick={() => openShopifyProduct(product?.id)}
                      >
                        Open in Shopify
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </InfiniteScroll>
      )}

      <Dialog
        open={lightbox.open}
        onClose={closeLightbox}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        <Box sx={{ position: "relative", textAlign: "center" }}>
          <IconButton
            onClick={closeLightbox}
            aria-label="Close image"
            sx={{
              position: "absolute",
              top: -40,
              right: 0,
              color: "common.white",
              bgcolor: "rgba(0,0,0,0.5)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          {lightbox.title ? (
            <Typography
              variant="subtitle1"
              sx={{ color: "common.white", mb: 1 }}
            >
              {lightbox.title}
            </Typography>
          ) : null}
          <Box
            component="img"
            src={lightbox.src}
            alt={lightbox.title || "Product"}
            sx={{
              maxWidth: "100%",
              maxHeight: "80vh",
              objectFit: "contain",
              borderRadius: 1,
            }}
          />
        </Box>
      </Dialog>
    </React.Fragment>
  );
}
